import React, { useEffect, useState, useReducer } from "react";

import * as dayjs from "dayjs";
import * as dayjsPluginUtc from "dayjs/plugin/utc";
import * as dayjsPluginDayOfYear from "dayjs/plugin/dayOfYear";

import {
  getEvents,
  getHistoricalEvents,
  getGeoData,
} from "../../../api/kquake";
import {
  EarthquakeFilterContext,
  earthquakeFilterDefaultState,
  earthquakeFilterReducer,
  getKQuakeEarthquakeApiFilter,
} from "../../../contexts/EarthquakeFilterContext";
import { get3dFault } from "../../../geoutils";
import { EarthquakeSelectorForm } from "../../forms/EarthquakeSelectorForm";
import {
  VALID_INTENSITIES,
  HistoricalEarthquakeSelectorForm,
} from "../../forms/HistoricalEarthquakeSelectorForm";
import { LoadingScreen } from "../../LoadingScreen";
import { Trans, useTranslation } from "react-i18next";

import { EARTHQUAKE_MARKER_SCALES, SeismicityMap } from "./SeismicityMap";

dayjs.extend(dayjsPluginUtc);
dayjs.extend(dayjsPluginDayOfYear);

const NUMBER_OF_SUPPORTING_3D_FAULTS = 10;

const PLOTLY_LEGEND_RANK_DEFAULT = 1000;
// const EARTHQUAKE_CATALOGS = ["KMA", "KIGAM", "HISTORICAL"];
// const EARTHQUAKE_CATALOGS = ["KMA", "KIGAM"];
const EARTHQUAKE_CATALOGS = ["KMA"];
// const MAJOR_FAULT_NAMES = {
//   Geumwang: "금왕",
//   Gongju: "공주",
//   Yangsan: "양산",
//   Yeongdeok: "영덕",
//   Wangsukchen: "왕숙천",
//   Gyeryongsan: "계룡사",
//   Chugaryeon: "추가령",
//   Moryang: "모량",
//   Jain: "자인",
//   Miryang: "밀양",
//   Ilgwang: "일광",
//   Ulsan: "울산",
//   Dongnae: "동래",
//   Sibjaga: "십자가",
//   Pocheon: "포천",
// };
const MAJOR_FAULTS = [
  "Geumwang",
  "Gongju",
  "Yangsan",
  "Yeongdeok",
  "Wangsukchen",
  "Gyeryongsan",
  "Chugaryeon",
  "Moryang",
  "Jain",
  "Miryang",
  "Ilgwang",
  "Ulsan",
  "Dongnae",
  "Sibjaga",
  "Pocheon",
];

const INITIAL_VIRTUAL_FAULT_SURFACE_VALUE = {
  type: "",
  depth: "",
  angle: "",
  start: { lat: "", lon: "" },
  end: { lat: "", lon: "" },
};
const VIRTUAL_FAULT_SURFACE_TYPES = ["major", "custom"];

const faultDecorations = {
  Wangsukche: { rank: 11, color: "rgb(255,45,0)" },
  Chugaryeon: { rank: 12, color: "rgb(255,45,0)" },
  Pocheon: { rank: 13, color: "rgb(255,45,0)" },

  Geumwang: { rank: 21, color: "rgb(110,255,0)" },
  Gongju: { rank: 22, color: "rgb(110,255,0)" },
  Gyeryongsa: { rank: 23, color: "rgb(110,255,0)" },
  Sibjaga: { rank: 24, color: "rgb(110,255,0)" },

  Yangsan: { rank: 31, color: "rgb(0,248,255)" },
  Yeongdeok: { rank: 32, color: "rgb(0,248,255)" },
  Moryang: { rank: 33, color: "rgb(0,248,255)" },
  Jain: { rank: 34, color: "rgb(0,248,255)" },
  Miryang: { rank: 35, color: "rgb(0,248,255)" },
  Ulsan: { rank: 36, color: "rgb(0,248,255)" },
  Dongnae: { rank: 37, color: "rgb(0,248,255)" },
  Ilgwang: { rank: 38, color: "rgb(0,248,255)" },
};

export const Seismicity = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [earthquakeCatalog, setEarthquakeCatalog] = useState("KMA");
  // const [earthquakeCatalogs, setEarthquakeCatalogs] = useState([]);
  // const handleEathquakeCatalogChanged = (event) => {
  //   if (event.target.checked) {
  //     setEarthquakeCatalogs([...earthquakeCatalogs, event.target.name]);
  //   }
  // }

  const [earthquakeFilter, dispatchEarthquakeFilter] = useReducer(
    earthquakeFilterReducer,
    earthquakeFilterDefaultState
  );
  const [earthquakes, setEarthquakes] = useState([]);
  const [earthquakeMarkerScale, setEarthquakeMarkerScale] = useState("medium");

  const [isHistoricalEqSelected, setIsHistoricalEqSelected] = useState(false);
  const [historicalEarthquakes, setHistoricalEarthquakes] = useState([]);
  const [historicalEarthquakeFilter, setHistoricalEarthquakeFilter] = useState({
    period: { enabled: false },
    intensities: [...VALID_INTENSITIES],
  });

  const loadEarthquakes = async () => {
    let earthquakeApiFilter = {};
    try {
      earthquakeApiFilter = getKQuakeEarthquakeApiFilter(earthquakeFilter);
    } catch (error) {
      alert(
        t("form.message.errorInEvent") +
          "\r\n" +
          t("form.message." + error.message)
      );
      return;
    }
    // console.log(earthquakeApiFilter);
    // setEarthquakes([]);
    setEarthquakes(null);
    setIsLoading(true);
    let earthquakes = [];
    try {
      earthquakes = await getEvents(earthquakeApiFilter);
    } catch (error) {
      const alertMessage =
        error.httpStatus === 204
          ? t("form.message.noEvents")
          : [
              t("form.message.failedToRetrieve"),
              `Error message: ${error.message}`,
              t("form.message.contactAdministrator"),
            ].join("\r\n");
      alert(alertMessage);
      setIsLoading(false);
      return;
    }

    if (earthquakes.length === 0) {
      window.alert(t("form.message.noEvents"));
      setIsLoading(false);
      return;
    } else if (
      earthquakes.length >= 10000 &&
      !window.confirm(
        `Too many earthquakes(${earthquakes.length}).\r\nContinue?`
      )
    ) {
      setIsLoading(false);
      return;
    }

    setEarthquakes(earthquakes);
    setIsLoading(false);
  };

  const loadHistoricalEarthquakes = async () => {
    const earthquakeApiFilter = {};
    try {
      if (historicalEarthquakeFilter.period?.enabled) {
        const startYear = historicalEarthquakeFilter.period?.start;
        const endYear = historicalEarthquakeFilter.period?.end;

        if (isNaN(startYear) && isNaN(endYear)) {
          throw new Error(t("form.message.noStartYearAndEndYearError"));
        }

        if (!isNaN(startYear) && !isNaN(endYear) && startYear > endYear) {
          throw new Error(t("form.message.startYearLaterThanEndYearError"));
        }

        if (startYear) {
          earthquakeApiFilter.start = startYear;
        }
        if (endYear) {
          earthquakeApiFilter.end = endYear;
        }
      }
      if (
        historicalEarthquakeFilter.intensities.length !==
        VALID_INTENSITIES.length
      ) {
        if (historicalEarthquakeFilter.intensities.length === 0) {
          throw new Error(t("form.message.noIntensityWarning"));
        }
        earthquakeApiFilter.intensity = historicalEarthquakeFilter.intensities;
      }
    } catch (error) {
      alert(error.message);
      return;
    }

    setHistoricalEarthquakes(null);
    setIsLoading(true);

    let earthquakes = [];
    try {
      earthquakes = await getHistoricalEvents(earthquakeApiFilter);
    } catch (error) {
      const alertMessage =
        error.httpStatus === 204
          ? t("form.message.noEvents")
          : [
              t("form.message.failedToRetrieve"),
              `Error message: ${error.message}`,
              t("form.message.contactAdministrator"),
            ].join("\r\n");
      alert(alertMessage);
      setIsLoading(false);
      return;
    }

    if (earthquakes.length === 0) {
      window.alert(t("form.message.noEvents"));
      setIsLoading(false);
      return;
    }

    setHistoricalEarthquakes(earthquakes);
    setIsLoading(false);
  };

  const [newVirtualFaultSurface, setNewVirtualFaultSurface] = useState(
    INITIAL_VIRTUAL_FAULT_SURFACE_VALUE
  );
  const [virtualFaultSurfaces, setVirtualFaultSurfaces] = useState([]);
  useEffect(() => {
    if (newVirtualFaultSurface.type === "major") {
      // Init select element
      // https://materializecss.com/select.html
      const elems = document.querySelectorAll("select");
      window.M.FormSelect.init(elems);
    }
  }, [newVirtualFaultSurface.type]);
  const handleNewVirtualFaultChanged = (event) => {
    const { name, value, checked } = event.target;
    if (name.startsWith("type.")) {
      const newType = checked ? name.split(".").at(-1) : "";
      setNewVirtualFaultSurface({ ...newVirtualFaultSurface, type: newType });
    } else if (name.includes(".")) {
      const [key1, key2] = name.split(".");
      setNewVirtualFaultSurface({
        ...newVirtualFaultSurface,
        [key1]: { ...newVirtualFaultSurface[key1], [key2]: value },
      });
    } else {
      setNewVirtualFaultSurface({ ...newVirtualFaultSurface, [name]: value });
    }
  };
  const addNewVirtualFault = () => {
    if (virtualFaultSurfaces.length >= NUMBER_OF_SUPPORTING_3D_FAULTS) {
      alert(
        t("page.interactive-map.alert.numberOf3dFaults", {
          limit: NUMBER_OF_SUPPORTING_3D_FAULTS,
        })
      );
      return;
    }
    const newSurface = { type: newVirtualFaultSurface.type };
    if (newSurface.type === "major") {
      if (!newVirtualFaultSurface.faultName) {
        alert(t("page.interactive-map.alert.selectFault"));
        return;
      }
      newSurface.name = newVirtualFaultSurface.faultName;
    } else if (newSurface.type === "custom") {
      const startLat = parseFloat(newVirtualFaultSurface.start?.lat);
      if (isNaN(startLat) || startLat < 33.0 || startLat > 43.0) {
        alert(t("page.interactive-map.alert.startLatError"));
        return;
      }
      newSurface.startLat = startLat;

      const startLon = parseFloat(newVirtualFaultSurface.start?.lon);
      if (isNaN(startLon) || startLon < 124.0 || startLon > 132.0) {
        alert(t("page.interactive-map.alert.startLonError"));
        return;
      }
      newSurface.startLon = startLon;

      const endLat = parseFloat(newVirtualFaultSurface.end?.lat);
      if (isNaN(endLat) || endLat < 33.0 || endLat > 43.0) {
        alert(t("page.interactive-map.alert.endLatError"));
        return;
      }
      newSurface.endLat = endLat;

      const endLon = parseFloat(newVirtualFaultSurface.end?.lon);
      if (isNaN(endLon) || endLon < 124.0 || endLon > 132.0) {
        alert(t("page.interactive-map.alert.endLonError"));
        return;
      }
      newSurface.endLon = endLon;
    } else {
      alert(t("page.interactive-map.alert.unknownFaultType"));
      return;
    }

    const depth = parseInt(newVirtualFaultSurface.depth);
    if (isNaN(depth) || depth <= 0 || depth > 30) {
      alert(t("page.interactive-map.alert.depthError"));
      return;
    }
    newSurface.depth = depth;

    const angle = parseInt(newVirtualFaultSurface.angle);
    if (isNaN(angle) || angle < -90 || angle > 90) {
      alert(t("page.interactive-map.alert.angleError"));
      return;
    }
    newSurface.angle = angle;

    setVirtualFaultSurfaces([...virtualFaultSurfaces, newSurface]);
    setNewVirtualFaultSurface(INITIAL_VIRTUAL_FAULT_SURFACE_VALUE);
  };
  const deleteVirtualFault = (faultIndex) => {
    setVirtualFaultSurfaces([
      ...virtualFaultSurfaces.slice(0, faultIndex),
      ...virtualFaultSurfaces.slice(faultIndex + 1),
    ]);
  };

  return (
    <div className="container">
      <div className="sub-container">
        <div style={{ fontSize: "15px", marginBottom: "5px" }}>
          &nbsp;&nbsp;<i className="ion ion-md-help-circle-outline"></i>
          {t("page.interactive-map.desc")}
        </div>
        <div className="overflow-auto">
          <table className="datatable mb20">
            <colgroup className="">
              <col style={{ width: "20%" }} />
              <col style={{ width: "80%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th className="first">{t("w.instruction")}</th>
                <td className="first clearfix">
                  <SeismicityInstrictions />
                </td>
              </tr>
              <tr>
                <th>{t("w.earthquake")}</th>
                <td className="clearfix">
                  <EarthquakeFilterContext.Provider
                    value={{
                      state: earthquakeFilter,
                      dispatch: dispatchEarthquakeFilter,
                    }}
                  >
                    <div>
                      <i className="ion ion-md-information-circle-outline"></i>
                      &nbsp;{t("page.interactive-map.instruction.earthquake")}
                      <br />
                      {/* 카탈로그:&nbsp;&nbsp; */}
                      {EARTHQUAKE_CATALOGS.map((catalog) => (
                        <label
                          key={catalog}
                          className="datatable_select_radio"
                          htmlFor={`events.catalog.${catalog}`}
                        >
                          <input
                            type="checkbox"
                            id={`events.catalog.${catalog}`}
                            name={catalog}
                            className="filled-in with-gap"
                            checked={earthquakeCatalog === catalog}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEarthquakeCatalog(catalog);
                              } else {
                                setEarthquakeCatalog("");
                              }
                            }}
                          />
                          <span>
                            {t(
                              `page.interactive-map.earthquakeCatalog.${catalog}`
                            )}
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                        </label>
                      ))}
                    </div>
                    {earthquakeCatalog && (
                      <>
                        <EarthquakeSelectorForm
                          filterTypes={["date", "magnitude"]}
                        />
                        <div style={{ marginTop: 10 }}>
                          <a
                            className="waves-effect waves-light button-sm modal-trigger"
                            onClick={loadEarthquakes}
                          >
                            {t("w.search")}
                          </a>
                        </div>
                        <div>
                          &nbsp;-{" "}
                          {t(
                            "page.interactive-map.earthquakeMarkerScale.title"
                          )}
                          :&nbsp;
                          {EARTHQUAKE_MARKER_SCALES.map((scale) => (
                            <label
                              key={scale}
                              className="datatable_select_radio"
                              htmlFor={`earthquakeMarkerScale.${scale}`}
                            >
                              <input
                                type="radio"
                                id={`earthquakeMarkerScale.${scale}`}
                                name="earthquakeMarkerScale"
                                value={scale}
                                className="with-gap"
                                checked={earthquakeMarkerScale === scale}
                                onChange={() => setEarthquakeMarkerScale(scale)}
                              />
                              <span>
                                {t(
                                  `page.interactive-map.earthquakeMarkerScale.${scale}`
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </EarthquakeFilterContext.Provider>
                  {/* <hr width="50%" align="left" /> */}
                  <div
                    style={{
                      marginTop: 15,
                      paddingTop: 15,
                      borderTop: "dashed 1px #A0A0A0",
                    }}
                  >
                    <i className="ion ion-md-information-circle-outline"></i>
                    &nbsp;
                    {
                      <Trans
                        i18nKey="page.interactive-map.instruction.historicalEarthquake"
                        components={{
                          link1: (
                            <a
                              href="https://www.weather.go.kr/w/eqk-vol/archive/historical-eqk.do"
                              target="_blank"
                              rel="noreferrer"
                            />
                          ),
                        }}
                      />
                    }
                    <br />
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.plotHistorical"
                    >
                      <input
                        type="checkbox"
                        id="events.plotHistorical"
                        className="filled-in with-gap"
                        checked={isHistoricalEqSelected}
                        onChange={(e) =>
                          setIsHistoricalEqSelected(e.target.checked)
                        }
                      />
                      <span>
                        {t("page.interactive-map.historicalEarthquake")}
                      </span>
                    </label>
                  </div>
                  {isHistoricalEqSelected && (
                    <>
                      <HistoricalEarthquakeSelectorForm
                        filter={historicalEarthquakeFilter}
                        setFilter={setHistoricalEarthquakeFilter}
                      />
                      <div style={{ marginTop: 15 }}>
                        <a
                          className="waves-effect waves-light button-sm modal-trigger"
                          onClick={loadHistoricalEarthquakes}
                        >
                          {t("w.search")}
                        </a>
                      </div>
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th>{t("page.interactive-map.hypoFaultPlane")}</th>
                <td>
                  <div>
                    <i className="ion ion-md-information-circle-outline"></i>
                    &nbsp;{t("page.interactive-map.instruction.faultSurface")}
                    <br />
                    {["major", "custom"].map((faultType) => (
                      <label
                        key={faultType}
                        className="datatable_select_radio"
                        htmlFor={`virtual3DFault.new.type.${faultType}`}
                      >
                        <input
                          type="checkbox"
                          id={`virtual3DFault.new.type.${faultType}`}
                          name={`type.${faultType}`}
                          className="filled-in with-gap"
                          checked={newVirtualFaultSurface?.type === faultType}
                          onChange={handleNewVirtualFaultChanged}
                        />
                        <span>
                          {t(`page.interactive-map.faultType.${faultType}`)}
                          &nbsp;&nbsp;&nbsp;&nbsp;
                        </span>
                      </label>
                    ))}
                    {newVirtualFaultSurface?.type === "major" && (
                      <>
                        <div className="row">
                          <div className="col data_limiter_container">
                            <select
                              name="faultName"
                              value=""
                              onChange={handleNewVirtualFaultChanged}
                            >
                              <option value="">{t("w.select")}</option>
                              {MAJOR_FAULTS.map((faultName) => (
                                <option key={faultName} value={faultName}>
                                  {t("faults." + faultName)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col" style={{ marginTop: 7 }}>
                            &nbsp;&nbsp;{t("w.depth")} (km):{" "}
                            <input
                              className="multiinput2"
                              type="number"
                              min="1"
                              max="30"
                              name="depth"
                              // placeholder="30"
                              aria-label="단층 깊이"
                              title="0 ~ 30"
                              placeholder="30"
                              value={newVirtualFaultSurface.depth}
                              onChange={handleNewVirtualFaultChanged}
                            />
                          </div>
                          <div className="col" style={{ marginTop: 7 }}>
                            &nbsp;&nbsp;{t("w.dip")} (º):
                            <input
                              className="multiinput2"
                              type="number"
                              min="-90"
                              max="90"
                              name="angle"
                              // placeholder="90"
                              aria-label="단층 경사"
                              title="-90 ~ 90"
                              placeholder="90"
                              value={newVirtualFaultSurface.angle}
                              onChange={handleNewVirtualFaultChanged}
                            />
                          </div>
                        </div>
                        <div>
                          <i className="ion ion-md-information-circle-outline"></i>
                          &nbsp;
                          {t("page.interactive-map.instruction.faultSurface1")}
                          <br />
                          {t("page.interactive-map.instruction.faultSurface2")}
                        </div>
                        <div style={{ marginTop: 15 }}>
                          <a
                            className="waves-effect waves-light button-sm modal-trigger"
                            onClick={addNewVirtualFault}
                          >
                            {t("page.interactive-map.addFaultSurface")}
                          </a>
                        </div>
                      </>
                    )}
                    {newVirtualFaultSurface?.type === "custom" && (
                      <>
                        <div className="row">
                          <div className="col">
                            {t("page.interactive-map.startPoint")}:
                            <input
                              className="multiinput2"
                              type="number"
                              min="33"
                              max="43"
                              step={0.000001}
                              name="start.lat"
                              title="33.0 ~ 43.0"
                              // placeholder="36.3"
                              aria-label="시작점 위도"
                              value={newVirtualFaultSurface.start.lat}
                              onChange={handleNewVirtualFaultChanged}
                            />
                            , &nbsp;
                            <input
                              className="multiinput2"
                              type="number"
                              min="124"
                              max="132"
                              step={0.000001}
                              name="start.lon"
                              title="124.0 ~ 132.0"
                              // placeholder="127.5"
                              aria-label="시작점 경도"
                              value={newVirtualFaultSurface.start.lon}
                              onChange={handleNewVirtualFaultChanged}
                            />
                          </div>
                          <div className="col">
                            {t("page.interactive-map.endPoint")}:
                            <input
                              className="multiinput2"
                              type="number"
                              min="33"
                              max="43"
                              step={0.000001}
                              name="end.lat"
                              title="33.0 ~ 43.0"
                              // placeholder="36.3"
                              aria-label="종료점 위도"
                              value={newVirtualFaultSurface.end.lat}
                              onChange={handleNewVirtualFaultChanged}
                            />
                            , &nbsp;
                            <input
                              className="multiinput2"
                              type="number"
                              min="124"
                              max="132"
                              step={0.000001}
                              name="end.lon"
                              title="124.0 ~ 132.0"
                              // placeholder="127.5"
                              aria-label="종료점 경도"
                              value={newVirtualFaultSurface.end.lon}
                              onChange={handleNewVirtualFaultChanged}
                            />
                          </div>
                          <div className="col">
                            {t("w.depth")}: (km):{" "}
                            <input
                              className="multiinput2"
                              type="number"
                              min="1"
                              max="30"
                              name="depth"
                              placeholder="30"
                              aria-label="단층 깊이"
                              title="0 ~ 30"
                              value={newVirtualFaultSurface.depth}
                              onChange={handleNewVirtualFaultChanged}
                            />
                          </div>
                          <div className="col">
                            {t("w.dip")} (º):
                            <input
                              className="multiinput2"
                              type="number"
                              min="-90"
                              max="90"
                              name="angle"
                              placeholder="90"
                              aria-label="단층 경사"
                              title="-90 ~ 90"
                              value={newVirtualFaultSurface.angle}
                              onChange={handleNewVirtualFaultChanged}
                            />
                          </div>
                        </div>
                        <div>
                          <i className="ion ion-md-information-circle-outline"></i>
                          &nbsp;
                          {t("page.interactive-map.instruction.faultSurface1")}
                          <br />
                          {t("page.interactive-map.instruction.faultSurface2")}
                        </div>
                        <div style={{ marginTop: 15 }}>
                          <a
                            className="waves-effect waves-light button-sm modal-trigger"
                            onClick={addNewVirtualFault}
                          >
                            {t("page.interactive-map.addFaultSurface")}
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 15,
                      paddingTop: 15,
                      borderTop: "dashed 1px #A0A0A0",
                    }}
                  >
                    {virtualFaultSurfaces.map((fault, faultIndex) => (
                      <div key={faultIndex} className="row">
                        {fault.type === "major" ? (
                          <span>
                            {faultIndex + 1}. {t("w.fault")}:{" "}
                            {t("faults." + fault.name)}, {t("w.depth")}:{" "}
                            {fault.depth} km, {t("w.dip")}: {fault.angle}º
                          </span>
                        ) : (
                          <span>
                            {faultIndex + 1}. {t("w.fault")}:{" "}
                            {fault.startLat.toFixed(6)}{" "}
                            {fault.startLon.toFixed(6)} ~{" "}
                            {fault.endLat.toFixed(6)} {fault.endLon.toFixed(6)},{" "}
                            {t("w.depth")}: {fault.depth} km, {t("w.dip")}:{" "}
                            {fault.angle}º
                          </span>
                        )}
                        <a
                          className="waves-effect waves-lignt button-sm modal-trigger"
                          style={{
                            marginLeft: 10,
                            backgroundColor: "rgba(255,0,0,0.5)",
                          }}
                          onClick={() => deleteVirtualFault(faultIndex)}
                        >
                          {t("w.remove")}
                        </a>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
          border: "1px solid rgba(0,0,0,0.12)",
        }}
      >
        <SeismicityMap
          earthquakes={earthquakes}
          earthquakeMarkerScale={earthquakeMarkerScale}
          historicalEarthquakes={historicalEarthquakes}
          faultSurfaces={virtualFaultSurfaces}
        />
        <br />
        <br />
      </div>
      <SeismicityReferences />
      <LoadingScreen screen={isLoading} />
    </div>
  );
};

const SeismicityInstrictions = () => {
  const { t } = useTranslation();
  return (
    <div>
      - {t("page.interactive-map.instruction.1")}
      <br /> - {t("page.interactive-map.instruction.2")}
      <br /> - {t("page.interactive-map.instruction.3")}
      <br /> - {t("page.interactive-map.instruction.4")}
      <br /> ※ {t("page.interactive-map.instruction.5")}
    </div>
  );
};

const SeismicityReferences = () => {
  const { t } = useTranslation();
  return (
    <div>
      ※ {t("w.dataSource")}
      <br />
      1. {t("w.adminRegions")}
      <br />
      &nbsp;&nbsp;-{" "}
      <Trans
        i18nKey="page.interactive-map.dataSources.adminSouth"
        components={{
          link1: (
            <a
              href="https://sgis.kostat.go.kr/view/index"
              target="_blank"
              rel="noreferrer"
            />
          ),
        }}
      />
      <br />
      &nbsp;&nbsp;-{" "}
      <Trans
        i18nKey="page.interactive-map.dataSources.adminNotrh"
        components={{
          link1: (
            <a
              href="https://data.humdata.org/dataset/cod-ab-prk"
              target="_blank"
              rel="noreferrer"
            />
          ),
        }}
      />
      <br />
      2. DEM (Digital Elevation Model)
      <br />
      &nbsp;&nbsp;&nbsp;&nbsp;-{" "}
      <a href="https://www.generic-mapping-tools.org/remote-datasets/earth-relief.html">
        IGPP Earth Relief
      </a>{" "}
      (10 arc minute global relief, SRTM15+V2.5.5)
      <br />
      3. {t("w.faults")}
      <br />
      &nbsp;&nbsp;-{" "}
      <Trans
        i18nKey="page.interactive-map.dataSources.faults"
        components={{
          link1: (
            <a
              href="https://data.kigam.re.kr/data/d1a66b54-0541-4830-9b7f-0ea847430643"
              target="_blank"
              rel="noreferrer"
            />
          ),
        }}
      />
      <br />※ {t("page.interactive-map.dataSources.note")}
    </div>
  );
};
